import RefundTransferDot from '../../../../Component/UI/RefundTransferDot';
import {
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useParams, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import api from "../../../../Component/axios_Interceptor/api";
import url from "../../../../Component/server";
import { fontStyles } from "../../../../Component/style/admin_font";
import { RowView2 } from "../../../../Component/style/common_style";

// 주문 상태 탭 (표시명 = 상태값 / key = URL)
export const ORDER_PAGES = [
  { name: "결제대기", key: "payment-wait" },
  { name: "결제완료", key: "payment-done" },
  { name: "발주완료", key: "order-done" },
  { name: "배송중", key: "shipping" },
  { name: "배송완료", key: "delivery-done" },
  { name: "주문취소", key: "cancel" },
  { name: "결제취소", key: "payment-cancel" },
  { name: "환불", key: "refund" },
  { name: "취소·반품·환불", key: "claims" },
  { name: "사고처리", key: "accident" },
  { name: "전체", key: "all" },
];

type Count = { cnt: number; user: number; bankPending?: number };

const Order_select_menu = forwardRef(
  (
    {
      go_page,
      onTabOffsetChange,
      onErrorChange,
      testCounts = {},
    }: {
      go_page: (key: string) => void;
      onTabOffsetChange?: (offsetLeft: number) => void;
      onErrorChange?:(message:string)=>void;
      testCounts?: Record<string, number>;
    },
    ref
  ) => {
    const { page = "" } = useParams();
    const [searchParams] = useSearchParams();
    const liveSeq = Number(searchParams.get("live_seq")) || 0;
    const [counts, setCounts] = useState<Record<string, Count>>({});
    const [countLoading, setCountLoading] = useState(true);
    const [countError, setCountError] = useState(false);
    const countRequest = useRef(0);
    const orderDoneTabRef = useRef<HTMLButtonElement | null>(null);

    useImperativeHandle(ref, () => ({ get_order_count }));

    const get_order_count = async () => {
      const request = ++countRequest.current;
      if (!liveSeq) { setCounts({}); setCountLoading(false); setCountError(false); return; }
      setCountLoading(true); setCountError(false);
      try {
        const res = await api.get(url.Communication_url.order_count, { params: { live_seq: liveSeq } });
        if (request !== countRequest.current) return;
        if (!Array.isArray(res.data)) throw new Error("Invalid order counts");
        const map: Record<string, Count> = {};
        res.data.forEach(
          (item: {
            user_count: string;
            cnt: string;
            order_status: string; bank_refund_pending_count?: number;
          }) => {
            if (![item.cnt, item.user_count].every(value => (typeof value === "number" || (typeof value === "string" && value.trim() !== ""))
              && Number.isInteger(Number(value)) && Number(value) >= 0)) throw new Error("Invalid order count");
            map[item.order_status] = {
              cnt: Number(item.cnt),
              user: Number(item.user_count), bankPending: Number(item.bank_refund_pending_count || 0),
            };
          }
        );
        setCounts(map);onErrorChange?.("");
      } catch (error) {
        if(request===countRequest.current){setCountError(true);onErrorChange?.("주문 건수를 불러오지 못했습니다.");}
      } finally {
        if (request === countRequest.current) setCountLoading(false);
      }
    };
    useEffect(() => {
      get_order_count();
      return () => { countRequest.current += 1; };
    }, [page, liveSeq]);

    useEffect(() => {
      const handleUpdateOffset = () => {
        if (orderDoneTabRef.current && onTabOffsetChange) {
          onTabOffsetChange(orderDoneTabRef.current.offsetLeft);
        }
      };
      // 약간의 지연(예: 렌더링 후 리플로우 고려)을 주어 정확한 offsetLeft를 가져올 수 있게 함
      const timer = setTimeout(handleUpdateOffset, 50);
      window.addEventListener("resize", handleUpdateOffset);
      return () => {
        clearTimeout(timer);
        window.removeEventListener("resize", handleUpdateOffset);
      };
    }, [counts, page, onTabOffsetChange]);

    return (
      <>
      {countError && !onErrorChange && <p role="alert">주문 건수를 불러오지 못했습니다. <button type="button" onClick={get_order_count}>건수 다시 불러오기</button></p>}
      <PageNameArea role="group" aria-label="주문 상태 필터">
        {ORDER_PAGES.filter(({ key }) => !["payment-cancel", "cancel", "refund"].includes(key)).map(({ name, key }) => (
          <button
            type="button"
            aria-pressed={page === key || (key === "claims" && ["cancel", "refund", "payment-cancel"].includes(page))}
            key={key}
            ref={key === "order-done" ? orderDoneTabRef : undefined}
            className={page === key || (key === "claims" && ["cancel", "refund", "payment-cancel"].includes(page)) ? "this" : ""}
            onClick={() => {
              if (page !== key) go_page(key);
            }}
          >
            <span>{name}{key === "claims" && !countLoading && !countError && Number(counts[name]?.bankPending) > 0 && <RefundTransferDot />}</span>{" "}<span className="order-status-count">{!liveSeq ? "방송 선택" : countLoading ? "조회 중" : countError ? "조회 실패" : `${(counts[name]?.user ?? 0) + (testCounts[name] || 0)}명(${(counts[name]?.cnt ?? 0) + (testCounts[name] || 0)}건)${testCounts[name] ? " · 테스트 포함" : ""}`}</span>
          </button>
        ))}
      </PageNameArea>
      </>
    );
  }
);

export default memo(Order_select_menu);

const PageNameArea = styled(RowView2).attrs<{ 'data-admin-tabs'?: string }>({ 'data-admin-tabs': '' })`
  margin-bottom: var(--space-4);
  flex-wrap: wrap;
  gap: var(--space-2);
  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    min-height: var(--control-lg);
    box-sizing: border-box;
    padding: 0 var(--space-3);
    font: inherit;
    font-size: var(--text-body);
    font-weight: 500;
    color: var(--color-text-secondary);
    background: var(--color-bg-surface);
    border: 1px solid var(--color-border-default);
    border-radius: var(--radius-md);
    cursor: pointer;
  }
  button.this {
    color: var(--color-text-link);
    background: var(--color-brand-subtle);
    border-color: var(--color-brand);
  }
  :root[data-admin-design] && {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
    width: 100%;
    max-width: 100%;
  }
  :root[data-admin-design] && > button {
    width: auto;
    min-width: max-content;
    flex: 0 0 auto;
    flex-direction: row;
    padding-inline: var(--space-3);
    gap: var(--space-2);
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
  button:focus-visible { outline: 2px solid var(--color-focus-ring); outline-offset: 2px; }
`;

